import type { ReauthMarker } from '@/lib/api/errors'

/**
 * Session tokens storage.
 *
 * Security notes:
 * - All tokens (Sprint JWT, EmJogo provider session, Score session) are
 *   sensitive and must ONLY live in this layer.
 * - We intentionally store in `localStorage` (not `document.cookie`) because
 *   we already must send them as request headers — cookies would add CSRF
 *   surface without reducing XSS risk. Shipping via Authorization header with
 *   a strict CSP gives us a cleaner threat model.
 * - We DO NOT persist admin tokens in the same slot as club tokens to avoid
 *   accidental mixing (different JWT audiences on the server).
 * - Any change to the persisted shape MUST bump STORAGE_VERSION so existing
 *   stale entries are cleared instead of silently mis-interpreted.
 */

const STORAGE_VERSION = 'v1'

const KEYS = {
  club: `sprint.${STORAGE_VERSION}.club`,
  admin: `sprint.${STORAGE_VERSION}.admin`,
  preferences: `sprint.${STORAGE_VERSION}.prefs`,
} as const

export interface ClubSession {
  /** Sprint access token (HS256 JWT, audience sprint-console). */
  accessToken: string
  clubname: string
  /** ISO 8601 timestamp from /auth/login. */
  expiresAt: string
  /** EmJogo provider session, shipped as X-Provider-Session. */
  providerSession: {
    token: string
    expiresAt: string | null
  }
  /** Score session (X-Score-Session). Only present after a successful 2-step Score login. */
  scoreSession: {
    token: string
    expiresAt: string | null
  } | null
}

export interface AdminSession {
  /** Admin access token (HS256 JWT, audience sprint-admin-console). */
  accessToken: string
  username: string
  expiresAt: string
}

export interface Preferences {
  language: 'pt' | 'en'
  theme: 'light' | 'dark' | 'system'
  matchSheetMode: 'wizard' | 'single-page'
}

const DEFAULT_PREFERENCES: Preferences = {
  language: 'pt',
  theme: 'system',
  matchSheetMode: 'single-page',
}

function safeGet(key: string): unknown {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as unknown
  } catch {
    return null
  }
}

function safeSet(key: string, value: unknown): void {
  try {
    if (value == null) {
      window.localStorage.removeItem(key)
      return
    }
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* quota exceeded / private mode: best-effort only */
  }
}

function isClubSession(value: unknown): value is ClubSession {
  if (!value || typeof value !== 'object') return false
  const v = value as Partial<ClubSession>
  return (
    typeof v.accessToken === 'string' &&
    v.accessToken.length > 0 &&
    typeof v.clubname === 'string' &&
    v.clubname.length > 0 &&
    typeof v.expiresAt === 'string' &&
    typeof v.providerSession === 'object' &&
    v.providerSession !== null &&
    typeof (v.providerSession as { token?: unknown }).token === 'string'
  )
}

function isAdminSession(value: unknown): value is AdminSession {
  if (!value || typeof value !== 'object') return false
  const v = value as Partial<AdminSession>
  return (
    typeof v.accessToken === 'string' &&
    v.accessToken.length > 0 &&
    typeof v.username === 'string' &&
    typeof v.expiresAt === 'string'
  )
}

export function readClubSession(): ClubSession | null {
  const raw = safeGet(KEYS.club)
  return isClubSession(raw) ? raw : null
}

export function writeClubSession(session: ClubSession): void {
  safeSet(KEYS.club, session)
}

export function clearClubSession(): void {
  safeSet(KEYS.club, null)
}

export function updateScoreSession(
  scoreSession: ClubSession['scoreSession'],
): ClubSession | null {
  const existing = readClubSession()
  if (!existing) return null
  const next: ClubSession = { ...existing, scoreSession }
  writeClubSession(next)
  return next
}

export function clearScoreSession(): ClubSession | null {
  return updateScoreSession(null)
}

export function readAdminSession(): AdminSession | null {
  const raw = safeGet(KEYS.admin)
  return isAdminSession(raw) ? raw : null
}

export function writeAdminSession(session: AdminSession): void {
  safeSet(KEYS.admin, session)
}

export function clearAdminSession(): void {
  safeSet(KEYS.admin, null)
}

export function readPreferences(): Preferences {
  const raw = safeGet(KEYS.preferences)
  if (!raw || typeof raw !== 'object') return DEFAULT_PREFERENCES
  const v = raw as Partial<Preferences>
  return {
    language:
      v.language === 'pt' || v.language === 'en'
        ? v.language
        : DEFAULT_PREFERENCES.language,
    theme:
      v.theme === 'light' || v.theme === 'dark' || v.theme === 'system'
        ? v.theme
        : DEFAULT_PREFERENCES.theme,
    matchSheetMode:
      v.matchSheetMode === 'wizard' || v.matchSheetMode === 'single-page'
        ? v.matchSheetMode
        : DEFAULT_PREFERENCES.matchSheetMode,
  }
}

export function writePreferences(prefs: Partial<Preferences>): Preferences {
  const next: Preferences = { ...readPreferences(), ...prefs }
  safeSet(KEYS.preferences, next)
  return next
}

export function mapReauthToMissingSession(
  marker: ReauthMarker,
): 'score' | 'emjogo' {
  return marker === 'score_auth_required' ? 'score' : 'emjogo'
}
